import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Gift, ArrowRight } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ReviewModal } from './ReviewModal';
import { ContestRulesDialog } from './ContestRulesDialog';
import { ContestCountdown } from './ContestCountdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Self-contained promo pill: shows the admin-controlled tagline + a "Leave a review" action.
// - contestRules=true  -> clicking opens the Contest Rules dialog (landing page).
// - contestReview=true -> opens the Contest Rules dialog in dashboard mode, then the review modal.
// - forceNavigate=true -> always routes to sign-up (Get Started).
// - default            -> logged-out visitors go to sign-up; logged-in users open the review modal.
// Renders nothing when the campaign toggle is off.
export function ReviewPromoBox({ variant = 'hero', onReviewChange, forceNavigate = false, contestRules = false, contestReview = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [promo, setPromo] = useState(null);
  const [open, setOpen] = useState(false);
  const [contestOpen, setContestOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/app-reviews/promo`).then(r => setPromo(r.data)).catch(() => {});
  }, []);

  // Auto-open the contest dialog when the user was routed here via ?contest=1 (either
  // clicked "Enter contest" while logged out then logged in, OR came from the review
  // banner while already logged in). Clears the param so a page refresh doesn't re-open
  // the dialog forever.
  useEffect(() => {
    if (!contestReview) return;
    if (searchParams.get('contest') !== '1') return;
    setContestOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('contest');
    setSearchParams(next, { replace: true });
  }, [contestReview, searchParams, setSearchParams]);

  if (!promo || !promo.enabled) return null;

  const handleClick = () => {
    if (contestRules || contestReview) { setContestOpen(true); return; }
    if (forceNavigate || !user) { navigate('/auth?mode=register'); return; }
    setOpen(true);
  };

  return (
    <>
      <button
        data-testid={`review-promo-${variant}`}
        onClick={handleClick}
        className="group animate-review-pulse w-full text-left rounded-2xl px-4 py-3 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-[0_4px_14px_0_rgba(139,92,246,0.35)] hover:shadow-[0_6px_20px_rgba(139,92,246,0.45)] hover:-translate-y-0.5 transition-all duration-200"
      >
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
            <Gift className="w-5 h-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold leading-snug">{promo.tagline}</p>
            <div className="flex items-center gap-2 mt-0.5 text-xs text-white/85">
              <span className="flex items-center gap-1 group-hover:gap-1.5 transition-all">Leave a review <ArrowRight className="w-3.5 h-3.5" /></span>
              <span className="text-white/50">·</span>
              <ContestCountdown className="text-white/90" />
            </div>
          </div>
        </div>
      </button>
      <ReviewModal open={open} onOpenChange={setOpen} onSubmitted={onReviewChange} />
      <ContestRulesDialog
        open={contestOpen}
        onOpenChange={setContestOpen}
        dashboard={contestReview}
        onContinueToReview={() => setOpen(true)}
      />
    </>
  );
}

export default ReviewPromoBox;
